import { Component, EventEmitter, Input, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { getValuesFromEnum } from "@app/class/Utilities";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { ISim, SimStatus, SimType } from "@app/interfaces/ISim";
import { ISimMapping, SimMappingType } from "@app/interfaces/ISimMapping";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { SimMappingService } from "@app/main/modules/foundation/_services/sim-mapping.service";
import { SimService } from "@app/main/modules/foundation/_services/sim.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, of, Subscription } from "rxjs";
import { finalize, switchMap, tap } from "rxjs/operators";
import { ICombinedSimMappingQueryOptions } from "../../_models/ICombinedSimMappingQueryOptions";
import './sim-data.component.scss';

@UntilDestroy()
@Component({
    selector: 'sim-data',
    template: require('./sim-data.component.html')
})
export class SimDataComponent {
    @Input() simId: number;
    @Input() serial: string;
    @Input() vendor: ProductVendor;

    @Output() simUpdated = new EventEmitter<ISim>();

    @ViewChild('confirmReassignTemplate') confirmReassignTemplate: TemplateRef<any>;
    //--

    currentSim: ISim;
    SimType = SimType;
    SimStatus = SimStatus;
    currentMapping: ISimMapping;
    newSimMapping: ISimMapping;

    simTypes = getValuesFromEnum(SimType).filter(value => value !== SimType.Unknown);

    showUpdateSim: boolean;
    newSimForm: FormGroup;
    newSim: ISim;
    newSimId: number;
    simExists: boolean = true;
    validationError: string = "";
    showCreateSimFormFields: boolean;
    simAlreadyAssigned: boolean;

    dialog: VDialogRef;
    loading: boolean;

    private simValidationSubscription: Subscription;
    constructor(
        private readonly simMappingService: SimMappingService,
        private readonly simService: SimService,
        private readonly translationService: TranslationService,
        private readonly dialogService: DialogService) { }

    ngOnInit(): void {
        this.loadSimData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['simId'] && changes['serial']) {
            this.loadSimData();
        }
    }

    showSimUpdateForm(): void {
        this.showUpdateSim = true;
        this.clearValidationErrors();
        this.createSimForm();

        this.newSimForm.statusChanges.pipe(untilDestroyed(this)).subscribe(status => {
            if (status === "INVALID") {
                this.clearValidationErrors();
                return;
            }

            if (!this.showCreateSimFormFields) {
                this.validateSim();
                return;
            }

            this.newSim = {
                Iccid: this.newSimForm.get('simIccid').value,
                Imsi: this.newSimForm.get('Imsi')?.value,
                Msisdn: this.newSimForm.get('Msisdn')?.value,
                Type: this.newSimForm.get('Type')?.value,
                Status: SimStatus.Active
            }
        })
    }

    cancelSimUpdate(): void {
        this.createSimForm();
        this.showUpdateSim = false;
        this.clearValidationErrors();
        this.clearNewSimDetails();
        this.simValidationSubscription.unsubscribe();
    }

    clearValidationErrors(): void {
        this.validationError = "";
        this.simAlreadyAssigned = false;
    }

    showCreateSimForm(): void {
        this.showCreateSimFormFields = true;
        this.clearValidationErrors();
        this.newSimForm.addControl('Type', new FormControl(null, Validators.required));
        this.newSimForm.addControl('Imsi', new FormControl(""));
        this.newSimForm.addControl('Msisdn', new FormControl(""));
    }

    onSimTypeChanged(type: SimType): void {
        this.newSimForm.get('Type').setValue(type);
    }

    addOrUpdateSim(): void {
        if (this.currentMapping) {
            this.simMappingService.endSimMapping(this.currentMapping).pipe(untilDestroyed(this)).subscribe();
        }

        if (this.newSimId) {
            this.createNewMapping(this.newSimId).pipe(
                untilDestroyed(this),
                tap(() => {
                    this.currentSim = this.newSim;
                    this.simId = this.currentSim.Id;
                    this.clearNewSimDetails();
                }),
                finalize(() => {
                    this.onSimUpdateCompleted();
                })
            ).subscribe();
            return;
        }

        // Sim is new and needs adding
        this.simService.updateSim(this.newSim).pipe(
            untilDestroyed(this),
            switchMap(sim => {
                this.simId = sim.Id;
                this.currentSim = sim;
                this.clearNewSimDetails();
                return this.createNewMapping(sim.Id);
            }),
            finalize(() => {
                this.onSimUpdateCompleted();
            })
        ).subscribe();
    }

    unassignSim(confirm: boolean): void {
        if (!confirm) {
            return;
        }

        this.simMappingService.endSimMapping(this.currentMapping).pipe(
            untilDestroyed(this),
            tap(() => {
                this.clearAllSimData();
                this.simUpdated.emit(null);
            })
        ).subscribe();
    }


    confirmReassign(): void {
        this.dialog = this.dialogService.open(this.confirmReassignTemplate, null);
    }

    confirmReassignAndCloseDialog(): void {
        this.simMappingService.endSimMapping(this.newSimMapping).pipe(
            untilDestroyed(this),
            finalize(() => {
                this.addOrUpdateSim();
                this.closeDialog();
            })
        ).subscribe()
    }

    closeDialog(): void {
        this.dialog.close();
    }

    //--

    private validateSim(): void {
        this.loading = true;
        this.clearValidationErrors();

        const newIccid: string = this.newSimForm.get('simIccid').value;

        this.simValidationSubscription = this.simService.getSimByIccid(newIccid).pipe(
            untilDestroyed(this),
            finalize(() => this.loading = false),
            switchMap(sim => {
                if (!sim) {
                    this.simExists = false;
                    this.validationError = this.translationService.t("FoundationUnits.SimDoesNotExist");
                    this.simId = null;
                    return of([]);
                }

                this.simExists = true;
                this.newSim = sim;
                this.newSimId = sim.Id;
                return this.simMappingService.getSimMappings({ SimId: sim.Id });
            }),
            tap(mappings => {
                if (!mappings.length) {
                    return;
                }

                this.newSimMapping = mappings.find(x => !x.EndUtc);

                if (this.newSimMapping) {
                    this.validationError = this.translationService.t("FoundationUnits.SimAlreadyAssigned", this.newSimMapping.Serial);
                    this.simAlreadyAssigned = true;
                };
            })).subscribe()
    }

    private clearNewSimDetails(): void {
        this.newSim = null;
    }

    private clearAllSimData(): void {
        this.currentSim = null;
        this.simId = null;
        this.currentMapping = null;
        this.clearNewSimDetails();
    }

    private createSimForm(): void {
        this.newSimForm = new FormGroup({
            simIccid: new FormControl("", [Validators.required, CustomFormValidators.simIccid])
        })
    }

    private onSimUpdateCompleted(): void {
        this.showCreateSimFormFields = false;
        this.showUpdateSim = false;
        this.simUpdated.emit(this.newSim);
    }

    private createNewMapping(simId: number): Observable<ISimMapping> {

        const newSimMapping: ISimMapping = {
            Id: 0,
            Serial: this.serial,
            SimId: simId,
            SimMappingType: this.isUnit() ? SimMappingType.Unit : SimMappingType.Peripheral
        }

        return this.simMappingService.addSimMapping(newSimMapping).pipe(
            untilDestroyed(this),
            tap(mapping => {
                this.currentMapping = mapping;
            }));
    }

    private isUnit(): boolean {
        const unitvendor = [
            ProductVendor.Calamp,
            ProductVendor.Aplicom,
            ProductVendor.Queclink,
            ProductVendor.Astra,
            ProductVendor.Falcom,
            ProductVendor.Trakm8
        ]

        return unitvendor.includes(this.vendor);
    }

    private loadSimData(): void {
        if (!this.simId && !this.serial) {
            console.warn("No SimId or Serial given, unable to get Sim Data")
            return;
        }

        this.getSimAndMappings();
    }


    private getSimAndMappings(): void {
        this.loading = true;
        const options: ICombinedSimMappingQueryOptions = {
            Serial: this.serial,
            IsActive: true
        }

        this.simMappingService.getSimMappings(options).pipe(
            finalize(() => this.loading = false),
            switchMap(mappings => {
                if (!mappings.length) {
                    return of(null);
                }
                this.currentMapping = mappings[0];
                return this.simService.getSim(mappings[0].SimId);
            }),
            tap(sim => {
                this.currentSim = sim;
                this.simId = sim?.Id;
            })
        ).subscribe();
    }
}
